// src/composables/useAlertEvents.ts
import { onMounted, onUnmounted } from 'vue';
import { toastController } from '@ionic/vue';
import mitt from 'mitt';
import { useFollowResponse } from '@/api/user/mutations/user-follow-respond';
import { useGroupInvitationResponse } from '@/api/group/mutations/use-group-invitation-response';
import { useUpdateAlertStatus } from '@/api/alert/mutations/use-update-alert-status';
import { AlertTypeEnum, type Alert } from '@/api/alert/types/alert';
import { FollowRequestStatus } from '@/api/user/types/FollowRequest';
import type { User } from '@/api/user/types/user';
import router, { RouteName } from '@/router';
import { useAlertClick } from './use-alert-click';

type AlertEvents = {
  alert: { alert: Alert; user: User };
};
export const alertEmitter = mitt<AlertEvents>();

export function useAlertEvents() {
  const { mutate: followResponse } = useFollowResponse();
  const { mutate: groupInviteResponse } = useGroupInvitationResponse();
  const { mutate: updateAlertStatus } = useUpdateAlertStatus();
  const onAlertClick = useAlertClick();

  const getButtons = (alert: Alert) => {
    switch (alert.type) {
      case AlertTypeEnum.follow:
        return [
          {
            text: 'Decline',
            role: 'cancel',
            handler: () => {
              followResponse({ status: FollowRequestStatus.rejected, userId: alert.data.userId! });
              updateAlertStatus({ alertId: alert.id, status: FollowRequestStatus.rejected });
            },
          },
          {
            text: 'Accept',
            role: 'approve',
            handler: () => {
              followResponse({ status: FollowRequestStatus.accepted, userId: alert.data.userId! });
              updateAlertStatus({ alertId: alert.id, status: FollowRequestStatus.accepted });
            },
          },
        ];
      case AlertTypeEnum.groupInvite:
        return [
          {
            text: 'Decline',
            role: 'cancel',
            handler: () => {
              groupInviteResponse({ groupId: alert.data.groupId as string, status: FollowRequestStatus.rejected });
              updateAlertStatus({ alertId: alert.id, status: FollowRequestStatus.rejected });
            },
          },
          {
            text: 'Accept',
            role: 'approve',
            handler: () => {
              groupInviteResponse({ groupId: alert.data.groupId as string, status: FollowRequestStatus.accepted });
              updateAlertStatus({ alertId: alert.id, status: FollowRequestStatus.accepted });
            },
          },
        ];
      case AlertTypeEnum.workout:
        return [
          {
            text: 'Start Workout',
            role: 'approve',
            handler: () => {
              router.push(`/${RouteName.App}/${RouteName.Feed}/${RouteName.Plan}/${alert.data.workoutEventId}`);
            },
          },
        ];
      default:
        return [];
    }
  };

  async function onAlert({ alert }: { alert: Alert }) {
    const message = `${alert.title}! ${alert.body ? '\n' + alert.body : ''}`;

    const toast = await toastController.create({
      message,
      buttons: getButtons(alert),
      duration: 5000,
      position: 'bottom',
      color: 'secondary',
      cssClass: 'text-left text-base text-white max-h-[90vh] cursor-pointer pointer-events-auto',
      swipeGesture: 'vertical',
    });

    await toast.present();

    // Add click event to the toast element
    toast.addEventListener('click', event => {
      // Prevent triggering click when buttons are clicked
      if (!(event.target as HTMLElement).closest('button')) {
        onAlertClick(alert);
        toast.dismiss();
      }
    });
  }

  onMounted(() => {
    alertEmitter.on('alert', onAlert);
  });

  onUnmounted(() => {
    alertEmitter.off('alert', onAlert);
  });
}
